import Agents32 from "@iconify-react/fluent-color/agents-32";
import BriefCase32 from "@iconify-react/fluent-color/briefcase-32";
import Cloud32 from "@iconify-react/fluent-color/cloud-32";
import ContentView32 from "@iconify-react/fluent-color/content-view-32";
import { Button, Flex, Grid, Modal, Steps, Typography, theme } from "antd";
import { useEffect, useState } from "react";

const STEPS = [
	{
		label: "Workspaces",
		Icon: ContentView32,
		color: "#1890ff",
		title: "Welcome to your Workspace",
		description:
			"A Workspace is your team's shared space. A collaborative hub where multiple Projects live side by side. Create one per team, product line, or client and invite members to collaborate across everything inside.",
	},
	{
		label: "Environments",
		Icon: Cloud32,
		color: "#1864ff",
		title: "Connect your Environments",
		description:
			"Link StratoNext to your real infrastructure, adding your environments. The platform can auto-discover running applications and builds a live map of your estate.",
	},
	{
		label: "Projects",
		Icon: BriefCase32,
		color: "#003ab9",
		title: "Projects",
		description:
			"A Project represents a software application, where documentation lives, incidents are reported, tasks are assigned. Start by creating a Project and link existing documentation.",
	},
	{
		label: "Agents",
		Icon: Agents32,
		color: "#0a1633",
		title: "Deploy Agents",
		description:
			"Run intelligent agents that work in the background: host them on StratoNext's infrastructure, push them remotely into your own environments, or run them locally on your machine to reuse your existing AI subscriptions.",
	},
];

export const welcomeWizardKey = (workspaceId?: string) =>
	workspaceId ? `artemis-welcome-seen-${workspaceId}` : "artemis-welcome-seen";

export function WelcomeWizard({
	open,
	onClose,
	storageKey = welcomeWizardKey(),
}: {
	open: boolean;
	onClose: () => void;
	storageKey?: string;
}) {
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;

	const [step, setStep] = useState(0);
	const { token } = theme.useToken();
	const current = STEPS[step];
	const isFirst = step === 0;
	const isLast = step === STEPS.length - 1;
	const { Icon } = current;

	useEffect(() => {
		if (open) setStep(0);
	}, [open]);

	const dismiss = () => {
		localStorage.setItem(storageKey, "1");
		onClose();
	};

	return (
		<Modal
			open={open}
			width={780}
			footer={null}
			closable={false}
			centered
			styles={
				{
					// body: { padding: 0 },
					//	content: { padding: 0, overflow: "hidden", borderRadius: 16 },
				}
			}
		>
			{/* Step 1: full-bleed gradient hero with logo + icon; others: tinted panel */}

			<Flex
				align="center"
				justify="center"
				style={{
					background: `${current.color}10`,
					borderBottom: `2px solid ${current.color}25`,
					padding: "36px 40px",
				}}
			>
				<Flex
					align="center"
					justify="center"
					style={{
						width: 88,
						height: 88,
						borderRadius: 18,
						background: "white",
						boxShadow: `0 4px 24px ${current.color}30`,
					}}
				>
					<Icon height={"52"} />
				</Flex>
			</Flex>

			{/* Step progress */}
			{!isMobile && (
				<Flex style={{ padding: "24px 40px 0" }}>
					<Steps
						current={step}
						size="small"
						items={STEPS.map((s) => ({ title: s.label }))}
						style={{ width: "100%" }}
					/>
				</Flex>
			)}

			{/* Step content */}
			<Flex
				vertical
				align="center"
				style={{
					padding: "28px 48px 8px",
					textAlign: "center",
					minHeight: 140,
				}}
			>
				<Typography.Title level={3} style={{ margin: "0 0 10px" }}>
					{current.title}
				</Typography.Title>
				<Typography.Text
					type="secondary"
					style={{ fontSize: 15, maxWidth: 520, lineHeight: 1.8 }}
				>
					{current.description}
				</Typography.Text>
			</Flex>

			{/* Footer navigation */}
			<Flex
				justify="space-between"
				align="center"
				style={{ padding: "20px 40px 32px" }}
			>
				<Typography.Link
					style={{ color: token.colorTextTertiary }}
					onClick={dismiss}
				>
					Skip tour
				</Typography.Link>
				<Flex gap={8}>
					{!isFirst && (
						<Button onClick={() => setStep((s) => s - 1)}>Back</Button>
					)}
					<Button
						type="primary"
						onClick={isLast ? dismiss : () => setStep((s) => s + 1)}
					>
						{isLast ? "Get started" : "Next"}
					</Button>
				</Flex>
			</Flex>
		</Modal>
	);
}
